import { mix } from 'polished';
import { IThemeColourPalette, IThemePalette3Gradients, IThemePalette4Gradients, IThemePalette5Gradients, IThemeLogo, ITheme } from './interfaces';
import { textSizes, text } from './typography';
import { breakpoint } from './screensize';
import { linkColours } from '../Typography/Link/Link';
import { tooltipColours } from '../Components/Tooltip/Tooltip';
import { panelColours } from '../Components/Panel/Panel';
import { loadingBarColours } from '../Animations/LoadingBar/LoadingBar';
import { avatarColours } from '../Components/Avatar/Avatar';
import { bannerColours } from '../Components/Banner/Banner';
import { buttonColours } from '../Components/Button/Button';
import { cardColours } from '../Components/Card/Card';
import { carouselColours } from '../Components/Carousel/Carousel';
import { modalColours } from '../Components/ModalProvider/ModalProvider';
import { navigationColours } from '../Components/Navigation/Navigation';
import { notificationColours } from '../Components/Notification/Notification';
import { optionButtonColours, optionRadioButtonColours } from '../Components/OptionButton/OptionButton';
import { tabColours } from '../Components/Tabs/TabStrip';
import { checkboxColours } from '../Forms/CheckBox/CheckBox';
import { inputColours } from '../Forms/Input/Input';
import { radioColours } from '../Forms/RadioButton/RadioButton';
import { selectColours } from '../Forms/Select/selectStyles';
import { iconColours } from '../Components/Icons/Icon';

export const createPalette5ColourVariations = (colour: string, mixToColour: string): IThemePalette5Gradients => ({
  100: colour,
  75: mix(0.75, colour, mixToColour),
  50: mix(0.5, colour, mixToColour),
  25: mix(0.25, colour, mixToColour),
  20: mix(0.2, colour, mixToColour)
});

export const createPalette4ColourVariations = (colour: string, mixToColour: string): IThemePalette4Gradients => ({
  100: colour,
  75: mix(0.75, colour, mixToColour),
  50: mix(0.5, colour, mixToColour),
  25: mix(0.25, colour, mixToColour)
});

export const createPalette3ColourVariations = (colour: string, mixToColour: string): IThemePalette3Gradients => ({
  100: colour,
  60: mix(0.6, colour, mixToColour),
  30: mix(0.3, colour, mixToColour)
});

export const createPaletteColourVariations = (palette: IThemeColourPalette, mixToColour: string) => {
  const primarySecondPalette = createPalette4ColourVariations(palette.primary.second, mixToColour);
  return {
    primary: {
      first: createPalette5ColourVariations(palette.primary.first, mixToColour),
      second: primarySecondPalette,
      text: createPalette5ColourVariations(palette.primary.text, mixToColour)
    },
    secondary: {
      first: createPalette4ColourVariations(palette.secondary.first, mixToColour),
      second: createPalette4ColourVariations(palette.secondary.second, mixToColour),
      third: createPalette4ColourVariations(palette.secondary.third, mixToColour)
    },
    neutral: {
      black: createPalette4ColourVariations(palette.neutral.black, mixToColour),
      gray: createPalette3ColourVariations(palette.neutral.gray, mixToColour),
      white: palette.neutral.white
    },
    alerts: {
      ...palette.alerts,
      notification: primarySecondPalette[50]
    },
    trading: palette.trading
  };
};

export const createThemeFromPalette = (sourcePalette: IThemeColourPalette) => {
  const palette = createPaletteColourVariations(sourcePalette, sourcePalette.neutral.white);
  return {
    yes: palette.alerts.success,
    no: palette.alerts.alert,
    tbc: palette.alerts.pending,
    trading: palette.trading,
    default: {
      text: palette.primary.text[100],
      background: palette.neutral.white,
      border: palette.primary.text[20],
      caret: palette.primary.text[100]
    },
    separator: {
      border: palette.primary.text[20]
    },
    ...avatarColours(palette),
    ...bannerColours(palette),
    ...buttonColours(palette),
    ...cardColours(palette),
    ...carouselColours(palette),
    ...checkboxColours(palette),
    ...iconColours(palette),
    ...inputColours(palette),
    ...linkColours(palette),
    ...loadingBarColours(palette),
    ...modalColours(palette),
    ...navigationColours(palette),
    ...notificationColours(palette),
    ...optionButtonColours(palette),
    ...optionRadioButtonColours(palette),
    ...panelColours(palette),
    ...radioColours(palette),
    ...selectColours(palette),
    ...tabColours(palette),
    ...tooltipColours(palette)
  };
};

export const createTheme = (name: string, palette: IThemeColourPalette, logo: IThemeLogo): ITheme => ({
  name: name,
  colours: createThemeFromPalette(palette),
  textSizes,
  text,
  logo,
  breakpoint
});
